import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { GlassCard } from '../components/GlassCard';
import { AuraLogo } from '../components/Brand';
import { useUserStore } from '../store/userStore';
import { DOMAIN_QUESTIONS } from '../data/constants';
import { saveDomainCheckin } from '../lib/api';

export default function DomainQuestionnaire() {
  const navigate = useNavigate();
  const { domain } = useParams();
  const { user, draftDomainAnswers, setDraftDomainAnswers, clearDraftDomainAnswers } = useUserStore();

  const domainKey = DOMAIN_QUESTIONS[domain] ? domain : 'sleep';
  const domainData = DOMAIN_QUESTIONS[domainKey];
  const questions = domainData.questions;

  const [index, setIndex] = useState(0);
  const [answers, setAnswers] = useState(draftDomainAnswers[domainKey] || {});
  const [submitting, setSubmitting] = useState(false);

  const q = questions[index];
  const progress = ((index + 1) / questions.length) * 100;

  useEffect(() => {
    if (Object.keys(answers).length) setDraftDomainAnswers(domainKey, answers);
  }, [answers, domainKey]); // eslint-disable-line

  const setAnswer = (val) => setAnswers((p) => ({ ...p, [q.id]: val }));
  const toggleMulti = (opt) => {
    const cur = answers[q.id] || [];
    setAnswer(cur.includes(opt) ? cur.filter((o) => o !== opt) : [...cur, opt]);
  };

  const canProceed = () => {
    const a = answers[q.id];
    if (a === undefined || a === null) return false;
    if (Array.isArray(a) && a.length === 0) return false;
    if (typeof a === 'string' && a.trim() === '') return false;
    return true;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await saveDomainCheckin({
        user_id: user.id,
        domain: domainKey,
        answers,
        date: new Date().toISOString().split('T')[0],
        created_at: new Date().toISOString(),
      });
      clearDraftDomainAnswers(domainKey);
      toast.success(`${domainData.title} check-in saved`);
      navigate('/app');
    } catch (e) {
      toast.error('Failed to save. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col" style={{ background: 'var(--aura-bg)' }}>
      {/* Header */}
      <header className="sticky top-0 z-20 bg-white/85 backdrop-blur-xl border-b border-[var(--aura-line)]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate('/app')}
            data-testid="questionnaire-close"
            className="grid place-items-center w-9 h-9 rounded-full bg-white border border-[var(--aura-line)] text-[var(--aura-ink-soft)]"
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <div className="grid place-items-center w-9 h-9 rounded-xl" style={{ background: domainData.bg, color: domainData.color }}>
            <i className={`fa-solid ${domainData.icon}`} />
          </div>
          <div className="font-semibold">{domainData.title}</div>
        </div>
        <div className="max-w-2xl mx-auto px-4 pb-3">
          <div className="h-1.5 rounded-full bg-[var(--aura-line)] overflow-hidden">
            <div className="h-full rounded-full transition-base" style={{ width: `${progress}%`, background: domainData.color }} />
          </div>
          <div className="text-center text-xs text-[var(--aura-ink-soft)] mt-1">
            Question {index + 1} of {questions.length}
          </div>
        </div>
      </header>

      {/* Question */}
      <div className="flex-1 max-w-2xl w-full mx-auto px-4 py-8">
        <GlassCard className="p-6 sm:p-8" key={q.id}>
          <div className="text-[11px] uppercase tracking-wider text-[var(--aura-ink-soft)] mb-2">Question {index + 1}</div>
          <h2 className="font-display text-2xl mb-6 leading-snug">{q.question}</h2>

          {q.type === 'scale' && (
            <div>
              <div className="flex justify-between text-xs text-[var(--aura-ink-soft)] mb-2">
                <span>{q.min ?? 0}</span>
                <span>Moderate</span>
                <span>{q.max ?? 5}</span>
              </div>
              <div className="flex gap-2">
                {Array.from({ length: (q.max ?? 5) + 1 }, (_, i) => i).map((v) => (
                  <button
                    key={v}
                    data-testid={`scale-${v}`}
                    onClick={() => setAnswer(v)}
                    className="flex-1 h-12 rounded-xl border-2 font-semibold transition-base"
                    style={
                      answers[q.id] === v
                        ? { background: domainData.color, borderColor: domainData.color, color: '#fff' }
                        : { borderColor: 'var(--aura-line)', color: 'var(--aura-ink)' }
                    }
                  >
                    {v}
                  </button>
                ))}
              </div>
            </div>
          )}

          {q.type === 'yesno' && (
            <div className="grid grid-cols-2 gap-3">
              {[{ v: true, t: 'Yes', icon: 'fa-circle-check' }, { v: false, t: 'No', icon: 'fa-circle-xmark' }].map((o) => (
                <button
                  key={o.t}
                  data-testid={`yesno-${o.t.toLowerCase()}`}
                  onClick={() => setAnswer(o.v)}
                  className="flex items-center justify-center gap-2 py-5 rounded-2xl border-2 font-semibold transition-base"
                  style={
                    answers[q.id] === o.v
                      ? { background: o.v ? domainData.color : '#94A3B8', borderColor: o.v ? domainData.color : '#94A3B8', color: '#fff' }
                      : { borderColor: 'var(--aura-line)' }
                  }
                >
                  <i className={`fa-solid ${o.icon}`} /> {o.t}
                </button>
              ))}
            </div>
          )}

          {q.type === 'multiselect' && (
            <div className="flex flex-col gap-2">
              {q.options.map((opt) => {
                const sel = (answers[q.id] || []).includes(opt);
                return (
                  <button
                    key={opt}
                    data-testid={`option-${opt.replace(/\s+/g, '-').toLowerCase()}`}
                    onClick={() => toggleMulti(opt)}
                    className="flex items-center gap-3 px-4 py-3 rounded-xl border-2 text-left transition-base"
                    style={sel ? { borderColor: domainData.color, background: domainData.bg } : { borderColor: 'var(--aura-line)' }}
                  >
                    <span
                      className="grid place-items-center w-5 h-5 rounded-md border-2 shrink-0"
                      style={sel ? { background: domainData.color, borderColor: domainData.color } : { borderColor: 'var(--aura-line)' }}
                    >
                      {sel && <i className="fa-solid fa-check text-white text-[10px]" />}
                    </span>
                    <span style={sel ? { color: domainData.color, fontWeight: 600 } : {}}>{opt}</span>
                  </button>
                );
              })}
            </div>
          )}

          {q.type === 'time' && (
            <div className="flex flex-col items-center">
              <input
                data-testid="time-input"
                type="time"
                value={answers[q.id] || ''}
                onChange={(e) => setAnswer(e.target.value)}
                className="text-2xl font-semibold text-center rounded-2xl border-2 border-[var(--aura-line)] px-6 py-4 outline-none focus:border-[var(--aura-purple)]"
              />
              <p className="text-xs text-[var(--aura-ink-soft)] mt-3">Select the time (24-hour)</p>
            </div>
          )}
        </GlassCard>

        {q.type === 'multiselect' && (
          <div className="text-center mt-4">
            <button onClick={() => setIndex((i) => Math.min(i + 1, questions.length - 1))} className="text-sm text-[var(--aura-ink-soft)] underline">
              Skip this question
            </button>
          </div>
        )}
      </div>

      {/* Footer nav */}
      <footer className="sticky bottom-0 bg-white/85 backdrop-blur-xl border-t border-[var(--aura-line)]">
        <div className="max-w-2xl mx-auto px-4 py-3 flex items-center justify-between">
          <button
            onClick={() => setIndex((i) => Math.max(0, i - 1))}
            disabled={index === 0}
            className="flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium disabled:opacity-40"
          >
            <i className="fa-solid fa-arrow-left" /> Previous
          </button>
          {index === questions.length - 1 ? (
            <button
              data-testid="questionnaire-complete"
              onClick={submit}
              disabled={submitting || !canProceed()}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
              style={{ background: domainData.color }}
            >
              {submitting ? <i className="fa-solid fa-circle-notch fa-spin" /> : <>Complete <i className="fa-solid fa-circle-check" /></>}
            </button>
          ) : (
            <button
              data-testid="questionnaire-next"
              onClick={() => setIndex((i) => i + 1)}
              disabled={!canProceed()}
              className="flex items-center gap-2 px-6 py-2.5 rounded-xl text-sm font-semibold text-white disabled:opacity-40"
              style={{ background: canProceed() ? domainData.color : '#CBD5E1' }}
            >
              Next <i className="fa-solid fa-arrow-right" />
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
